import React from 'react';
import { getDisplayName } from '@/lib/userDisplayUtils';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusSelector } from '@/components/presence/StatusSelector';
import { User, LogOut, Settings, Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MemberData {
  name: string;
  status: string;
  subscription_type: string;
  join_date: string;
  image?: string;
}

export const UserStatusIndicator: React.FC = React.memo(() => {
  const { user, member, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const getUserName = () => {
    return getDisplayName({
      firstname: member?.firstname,
      lastname: member?.lastname,
      name: member?.name,
      email: user?.email
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleBadgeVariant = () => {
    return isAdmin ? 'destructive' : 'secondary';
  };

  const getRoleText = () => {
    return isAdmin ? 'Admin' : 'Membre';
  };

  const getStatusColor = () => {
    if (member?.status === 'actif') {
      return 'text-green-500';
    }
    return 'text-red-500';
  };

  const isAccountUpToDate = () => {
    return member?.status === 'actif';
  };

  const userName = getUserName();

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="relative h-auto p-2 bg-white hover:bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member?.image || undefined} />
                  <AvatarFallback className="text-xs bg-singu-yellow text-singu-dark">
                    {getInitials(userName)}
                  </AvatarFallback>
                </Avatar>
                <Circle 
                  className={`absolute -bottom-1 -right-1 h-3 w-3 fill-current ${getStatusColor()}`}
                />
              </div>
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {userName}
                  </span>
                  <Badge variant={getRoleBadgeVariant()} className="text-xs px-2 py-0.5 font-semibold">
                    {getRoleText()}
                  </Badge>
                </div>
              </div>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-2">
          <StatusSelector className="w-full" />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <User className="mr-2 h-4 w-4" />
          <span>Mon Profil</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/subscription')}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Mon Abonnement</span>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate('/admin')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Administration</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Déconnexion</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
  );
});